/**
 * M2 P2/P5: docked-services overlay. A pointer-events DOM panel shown while the
 * ship is DOCKED. P2: Refuel/Repair (functional). P5: Market (buy/sell at this
 * station's deterministic prices) + Missions (accept a courier; turn it in at the
 * destination with the cargo). Economy state lives in the Sim; the menu reads/
 * writes it via the EconomyBinding accessors (trade ops return new state).
 */
import './servicesMenu.css';
import {
  COMMODITIES,
  priceAt,
  tradeBuy,
  tradeSell,
  commodityById,
  heldQty,
  cargoUsed,
  removeCargo,
  credit,
  missionComplete,
  type StationSpec,
  type StationService,
  type Mission,
  type Wallet,
  type CargoHold,
  type Objective,
} from '@blackmarble/sim-core';
import type { ShipController } from '../sim/shipController.js';
import type { CloudSaves } from '../save/cloudSave.js';

/** Accessors so the menu can mutate the Sim-owned economy state. */
export interface EconomyBinding {
  getWallet(): Wallet;
  setWallet(w: Wallet): void;
  getCargo(): CargoHold;
  setCargo(c: CargoHold): void;
  getMissions(): Mission[];
  getActiveMission(): Mission | null;
  setActiveMission(m: Mission | null): void;
  // M3 WORLD B6: read-only objectives feed for the Tasks tab.
  getObjectives(): Objective[];
}

export class ServicesMenu {
  private readonly root: HTMLDivElement;
  private open = false;
  private currentSpec: StationSpec | null = null;

  constructor(
    private readonly ship: ShipController,
    private readonly econ: EconomyBinding,
    /** N2: optional cloud-record keeper (null on local-only builds). */
    private readonly cloud: CloudSaves | null = null,
  ) {
    this.root = document.createElement('div');
    this.root.id = 'svc-menu';
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(spec: StationSpec): void {
    this.currentSpec = spec;
    this.open = true;
    this.root.style.display = 'block';
    // UI dignity: Rearm has no ordnance behind it yet — render the honest ledger
    // line, disabled, instead of a live button that does nothing.
    const services = spec.services
      .map((s) =>
        s === 'Rearm'
          ? `<button class="svc-sealed" data-svc="${s}" disabled aria-disabled="true">Rearm — NO ORDNANCE REGISTERED</button>`
          : `<button data-svc="${s}">${s}</button>`,
      )
      .join('');
    const missions = spec.services.includes('Market')
      ? `<button data-svc="Missions">Missions</button>`
      : '';
    // M3 WORLD B6: Tasks tab available at every port (objectives are global).
    const tasks = `<button data-svc="Tasks">Tasks</button>`;
    // N2: the Record Keeper (cloud saves) — present whenever cloud is possible.
    const record = this.cloud?.enabled ? `<button data-svc="Record">Record</button>` : '';
    this.root.innerHTML =
      `<h2>${spec.name.toUpperCase()}</h2>${services}${missions}${tasks}${record}` +
      `<button class="close" data-svc="close">Close [G]</button>`;
    this.bind();
  }

  hide(): void {
    this.open = false;
    this.currentSpec = null;
    this.root.style.display = 'none';
    this.root.innerHTML = '';
  }

  /** Wire click handlers for whichever panel is currently rendered. */
  private bind(): void {
    this.root.querySelectorAll('button').forEach((b) => {
      const svc = b.getAttribute('data-svc');
      const buy = b.getAttribute('data-buy');
      const sell = b.getAttribute('data-sell');
      const accept = b.getAttribute('data-accept');
      const turnin = b.getAttribute('data-turnin');
      b.addEventListener('click', () => {
        if (svc) this.invoke(svc as StationService | 'close' | 'Missions' | 'Tasks' | 'back');
        else if (buy) this.buy(buy);
        else if (sell) this.sell(sell);
        else if (accept) this.accept(accept);
        else if (turnin) this.turnIn(turnin);
      });
    });
  }

  private invoke(svc: StationService | 'close' | 'Missions' | 'Tasks' | 'Record' | 'back'): void {
    switch (svc) {
      case 'Refuel': this.ship.refuelFull(); break;
      case 'Repair': this.ship.repairFull(); break;
      case 'Market': this.renderMarket(); break;
      case 'Missions': this.renderMissions(); break;
      case 'Tasks': this.renderTasks(); break; // M3 WORLD B6
      case 'Record': this.renderRecord(); break; // N2: the Record Keeper
      case 'back': if (this.currentSpec) this.show(this.currentSpec); break;
      case 'Rearm': break; // stub — ordnance arrives later
      case 'close': this.hide(); break;
    }
  }

  /** N2: the Record Keeper — sign in/register/sign out + sync, sombre register. */
  private renderRecord(): void {
    const c = this.cloud;
    if (!c) return;
    const body = c.signedIn
      ? `<div class="rk-status">${c.status}</div>` +
        `<button data-rk="sync">SYNC NOW</button>` +
        `<button data-rk="out">SIGN OUT</button>`
      : `<div class="rk-status">${c.status}</div>` +
        `<input id="rk-email" type="email" placeholder="EMAIL" autocomplete="username">` +
        `<input id="rk-pass" type="password" placeholder="PASSPHRASE" autocomplete="current-password">` +
        `<button data-rk="in">SIGN IN</button>` +
        `<button data-rk="reg">REGISTER</button>`;
    this.root.innerHTML =
      `<h2>RECORD KEEPER</h2>` +
      `<div class="rk-note">THE RECORD IS KEPT OFF-STATION. LOCAL COPY ALWAYS HOLDS.</div>` +
      body +
      `<button data-svc="back">Back</button><button class="close" data-svc="close">Close [G]</button>`;
    this.bind();
    this.root.querySelectorAll('[data-rk]').forEach((b) => {
      b.addEventListener('click', () => void this.recordAction(b.getAttribute('data-rk')!));
    });
  }

  /** N2: Record Keeper actions (async; re-renders with the resulting status). */
  private async recordAction(action: string): Promise<void> {
    const c = this.cloud;
    if (!c) return;
    const email = (this.root.querySelector('#rk-email') as HTMLInputElement | null)?.value ?? '';
    const pass = (this.root.querySelector('#rk-pass') as HTMLInputElement | null)?.value ?? '';
    if (action === 'in') await c.signIn(email, pass);
    else if (action === 'reg') await c.register(email, pass);
    else if (action === 'out') await c.signOut();
    else if (action === 'sync') await c.flush();
    this.renderRecord();
  }

  /** M3 WORLD B6: the Tasks tab — read-only list of objectives + status + reward. */
  private renderTasks(): void {
    const rows = this.econ.getObjectives().map((o) => this.taskRow(o)).join('');
    const body = rows || `<tr><td colspan="3">NO ACTIVE TASKS</td></tr>`;
    this.root.innerHTML =
      `<h2>TASKS</h2>${this.econHead()}<table>${body}</table>` +
      `<button data-svc="back">Back</button><button class="close" data-svc="close">Close [G]</button>`;
    this.bind();
  }

  /** One Tasks-tab row: briefing, progress, status badge + reward. */
  private taskRow(o: Objective): string {
    const prog = o.goal > 1 ? `${o.progress}/${o.goal}` : '';
    const badge =
      o.status === 'COMPLETE' ? `<span class="reconciled">RECONCILED +${o.reward}</span>`
      : o.status === 'FAILED' ? `<span class="lost">FAILED</span>`
      : `<span>+${o.reward}</span>`;
    return `<tr><td>${o.briefing}</td><td>${prog}</td><td>${badge}</td></tr>`;
  }

  private econHead(): string {
    const w = this.econ.getWallet();
    const c = this.econ.getCargo();
    return `<div class="econ-head">LEDGER ${w.credits} · MANIFEST ${cargoUsed(c)}/${c.capacity}</div>`;
  }

  private renderMarket(): void {
    const id = this.currentSpec?.id ?? '';
    const cargo = this.econ.getCargo();
    const rows = COMMODITIES.map((c) => {
      const p = priceAt(c, id);
      return (
        `<tr><td>${c.name}</td><td>${p}</td><td>${heldQty(cargo, c.id)}</td>` +
        `<td><button data-buy="${c.id}">DRAW 1</button></td>` +
        `<td><button data-sell="${c.id}">RETURN 1</button></td></tr>`
      );
    }).join('');
    this.root.innerHTML =
      `<h2>REQUISITION</h2>${this.econHead()}<table>${rows}</table>` +
      `<button data-svc="back">Back</button><button class="close" data-svc="close">Close [G]</button>`;
    this.bind();
  }

  private buy(commodityId: string): void {
    const c = commodityById(commodityId);
    if (!c || !this.currentSpec) return;
    const r = tradeBuy(this.econ.getWallet(), this.econ.getCargo(), commodityId, 1, priceAt(c, this.currentSpec.id));
    if (r.ok) {
      this.econ.setWallet(r.wallet);
      this.econ.setCargo(r.cargo);
    }
    this.renderMarket();
  }

  private sell(commodityId: string): void {
    const c = commodityById(commodityId);
    if (!c || !this.currentSpec) return;
    const r = tradeSell(this.econ.getWallet(), this.econ.getCargo(), commodityId, 1, priceAt(c, this.currentSpec.id));
    if (r.ok) {
      this.econ.setWallet(r.wallet);
      this.econ.setCargo(r.cargo);
    }
    this.renderMarket();
  }

  private missionRow(m: Mission): string {
    const active = this.econ.getActiveMission();
    const cargo = this.econ.getCargo();
    const cName = commodityById(m.commodityId)?.name ?? m.commodityId;
    const desc = `${m.qty}× ${cName}: ${m.fromStationId} → ${m.toStationId} (+${m.reward})`;
    let action: string;
    if (active?.id === m.id && this.currentSpec && missionComplete(m, this.currentSpec.id, cargo))
      action = `<button data-turnin="${m.id}">RECONCILE +${m.reward}</button>`;
    else if (!active) action = `<button data-accept="${m.id}">TAKE</button>`;
    else if (active.id === m.id) action = `<span class="active">CARRYING</span>`;
    else action = `<span class="active">—</span>`;
    return `<tr><td>${desc}</td><td>${action}</td></tr>`;
  }

  private renderMissions(): void {
    const rows = this.econ.getMissions().map((m) => this.missionRow(m)).join('');
    this.root.innerHTML =
      `<h2>CONSIGNMENTS</h2>${this.econHead()}<table>${rows}</table>` +
      `<button data-svc="back">Back</button><button class="close" data-svc="close">Close [G]</button>`;
    this.bind();
  }

  private accept(id: string): void {
    const m = this.econ.getMissions().find((x) => x.id === id) ?? null;
    this.econ.setActiveMission(m);
    this.renderMissions();
  }

  private turnIn(id: string): void {
    const m = this.econ.getActiveMission();
    if (!m || m.id !== id || !this.currentSpec || !missionComplete(m, this.currentSpec.id, this.econ.getCargo()))
      return;
    this.econ.setCargo(removeCargo(this.econ.getCargo(), m.commodityId, m.qty));
    this.econ.setWallet(credit(this.econ.getWallet(), m.reward));
    this.econ.setActiveMission(null);
    this.renderMissions();
  }
}
